import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import { getAccessibleMenu, menuConfig } from "../../config/menuConfig";
import "./Landing.css";

// IoT network background (central house + connected devices)
import imgLandingBg from "../../assets/landing-bg-iot.png";

/**
 * Capitalize first letter of each word; rest lowercase.
 * Works for "gudi suresh", "GUDI SURESH", or username like "gudi.suresh".
 */
const formatDisplayName = (firstName, lastName, username) => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (first || last) {
    const parts = [];
    if (first) parts.push(first.charAt(0).toUpperCase() + first.slice(1).toLowerCase());
    if (last) parts.push(last.charAt(0).toUpperCase() + last.slice(1).toLowerCase());
    return parts.join(" ").trim();
  }
  if (!username) return "";
  const parts = String(username).split(/[._\s]+/).filter(Boolean);
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ").trim();
};

/**
 * Lightweight dashboard-style SVG for hero (no external assets).
 * Grid + devices metaphor - minimal and professional.
 */
const HeroIllustration = () => (
  <svg
    width="120"
    height="80"
    viewBox="0 0 120 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ maxWidth: "100%", height: "auto" }}
  >
    <rect x="8" y="8" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.9" />
    <rect x="8" y="40" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.7" />
    <rect x="76" y="8" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.7" />
    <rect x="76" y="40" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.9" />
    <circle cx="26" cy="20" r="2.5" fill="currentColor" opacity="0.6" />
    <circle cx="94" cy="52" r="2.5" fill="currentColor" opacity="0.6" />
    <path d="M44 20h28M44 52h28" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
  </svg>
);

/**
 * Default landing page shown after login for all user roles.
 * Renders feature sections (aligned with sidebar menu) and shows only
 * sections/items the user has access to (UI-level conditional rendering).
 * Enhanced with subtle animations, section icons, and a clean hero area.
 */
const Landing = ({ userLevel, userRoles, tenantInfo, firstName, lastName, username }) => {
  const userContext = useMemo(
    () => ({
      userLevel: userLevel ?? 0,
      roles: userRoles ?? [],
      tenantId: tenantInfo?.tenantId ?? "",
    }),
    [userLevel, userRoles, tenantInfo]
  );

  const accessibleMenu = useMemo(
    () => getAccessibleMenu(menuConfig, userContext),
    [userContext]
  );

  const displayName = useMemo(
    () => formatDisplayName(firstName, lastName, username),
    [firstName, lastName, username]
  );

  return (
    <ResponsiveContentWrapper
      className="landing-content-wrapper"
      sx={{
        bgcolor: "#b0b0b0",
        paddingTop: 0,
        marginTop: "65px", // 64px header + 1px gap
        height: "calc(100vh - 65px)",
        maxHeight: "calc(100vh - 65px)",
        overflowY: "auto",
      }}
    >
      <Box
        className="landing-page-root"
        style={{ backgroundColor: "#b0b0b0", minHeight: "100%", paddingTop: 0, marginTop: 0 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          pt: 0,
          mt: 0,
          pb: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2, md: 2.5 },
          bgcolor: "#b0b0b0",
        }}
      >
        {/* Hero: IoT background image with welcome layout overlay (2nd image style) */}
        <Box
          className="landing-hero"
          sx={{
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            marginTop: 0,
            marginBottom: { xs: 0.5, md: 1 },
            minHeight: { xs: 120, sm: 140, md: 160 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            backgroundImage: `url(${imgLandingBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark overlay so text stays readable */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.45) 100%)",
              pointerEvents: "none",
            }}
          />
          {/* Content: Welcome left + grid right (like 2nd image) */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 2, sm: 3, md: 4 },
              width: "100%",
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1.5, sm: 2, md: 2 },
            }}
          >
            <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 600,
                  marginBottom: 0.5,
                  color: "#fff",
                  letterSpacing: "-0.02em",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                }}
              >
                Welcome{displayName ? `, ${displayName}` : ""}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 480,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.92)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                Choose a section below to get started. You only see areas you have access to.
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                color: "rgba(255,255,255,0.95)",
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HeroIllustration />
            </Box>
          </Box>
        </Box>

        {/* Section cards: flex to fill remaining space, no scroll */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Grid container spacing={{ xs: 1.5, sm: 1.5, md: 2 }} sx={{ flex: 1, alignContent: "flex-start" }}>
          {accessibleMenu.map((section, index) => {
            const SectionIcon = section.icon;
            return (
              <Grid item xs={12} sm={6} md={6} lg={4} key={section.id}>
                <Card
                  className="landing-card"
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    borderWidth: 1,
                    animationDelay: `${index * 0.06}s`,
                    "&:hover": {
                      boxShadow: 3,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, "&:last-child": { pb: 1.5 }, pt: 1.5, px: 2, pb: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginBottom: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: 1.25,
                          bgcolor: "action.hover",
                          color: "primary.main",
                        }}
                      >
                        {SectionIcon && <SectionIcon fontSize="small" />}
                      </Box>
                      <Typography
                        component="h2"
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {section.name}
                      </Typography>
                    </Box>
                    <List disablePadding>
                      {section.subitems?.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <ListItem
                            key={item.id}
                            component={Link}
                            to={item.path}
                            disablePadding
                            className="landing-link-item"
                            sx={{
                              color: "text.primary",
                              textDecoration: "none",
                              borderRadius: 1,
                              py: 0.5,
                              pl: 0.5,
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
                              {ItemIcon ? <ItemIcon fontSize="small" /> : null}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                variant: "body2",
                                fontWeight: 500,
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          </Grid>
        </Box>

        {accessibleMenu.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            className="landing-hero"
            sx={{ mt: 2 }}
          >
            No sections are available for your role. Contact your administrator if you need access.
          </Typography>
        )}
      </Box>
    </ResponsiveContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  userLevel: state.auth.userLevel,
  userRoles: state.auth.userRoles,
  tenantInfo: state.tenant.tenantInfo,
  firstName: state.auth.firstName,
  lastName: state.auth.lastName,
  username: state.auth.username,
});

export default connect(mapStateToProps)(Landing);
